import { z } from "zod";
import { zId, zIsoDate, zQueryBoolean } from "./common.js";

// ---- notes ----
export const zNote = z.object({
  id: zId,
  constituentId: zId,
  bodyRich: z.unknown().describe("TipTap JSON document"),
  bodyText: z.string().describe("plain-text extraction for search"),
  pinned: z.boolean(),
  isPrivate: z.boolean(),
  createdById: zId,
  createdByName: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Note = z.infer<typeof zNote>;

export const zCreateNote = z.object({
  bodyRich: z.unknown(),
  bodyText: z.string().max(50_000).default(""),
  pinned: z.boolean().default(false),
  isPrivate: z.boolean().default(false),
});

export const zUpdateNote = z.object({
  bodyRich: z.unknown().optional(),
  bodyText: z.string().max(50_000).optional(),
  pinned: z.boolean().optional(),
  isPrivate: z.boolean().optional(),
});

// ---- tasks ----
export const TASK_STATUSES = ["open", "in_progress", "done", "cancelled"] as const;
export const zTaskStatus = z.enum(TASK_STATUSES);

export const zTask = z.object({
  id: zId,
  title: z.string(),
  description: z.string().nullable(),
  status: zTaskStatus,
  dueDate: zIsoDate.nullable(),
  constituentId: zId.nullable(),
  constituentName: z.string().nullable().optional(),
  assigneeId: zId.nullable(),
  assigneeName: z.string().nullable().optional(),
  acknowledgmentGiftId: zId.nullable().optional(),
  createdById: zId,
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Task = z.infer<typeof zTask>;

export const zCreateTask = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().max(10_000).optional(),
  dueDate: zIsoDate.optional(),
  /** Must be a live constituent in the caller's org. */
  constituentId: zId.optional(),
  /** Owner (org user). Omitted → the current user; null → unassigned. */
  assigneeId: zId.nullable().optional(),
});
export type CreateTask = z.infer<typeof zCreateTask>;

export const zUpdateTask = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  description: z.string().max(10_000).nullable().optional(),
  status: zTaskStatus.optional(),
  dueDate: zIsoDate.nullable().optional(),
  constituentId: zId.nullable().optional(),
  assigneeId: zId.nullable().optional(),
});
export type UpdateTask = z.infer<typeof zUpdateTask>;

export const zTaskListQuery = z.object({
  status: zTaskStatus.optional(),
  assigneeId: zId.optional(),
  constituentId: zId.optional(),
  mine: zQueryBoolean.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

// ---- timeline ----
export const TIMELINE_KINDS = [
  "gift",
  "note",
  "task",
  "email",
  "event",
  "pledge",
  "grant",
  "membership",
  "volunteer",
  "sms",
  "mail",
  "move",
  "document",
  "form",
  "merge",
  "import",
  "receipt",
  "acknowledgment",
  /** CT-RM-20: posted by a partner app through /timeline-events */
  "app",
  /** CT-RM-34: linked to (or unlinked from) a custom object record */
  "custom_object",
] as const;
export const zTimelineKind = z.enum(TIMELINE_KINDS);
export type TimelineKind = z.infer<typeof zTimelineKind>;

export const zTimelineEntry = z.object({
  id: zId,
  constituentId: zId,
  kind: zTimelineKind,
  title: z.string(),
  summary: z.string().nullable(),
  refTable: z.string().nullable(),
  refId: zId.nullable(),
  actorId: zId.nullable(),
  actorName: z.string().nullable().optional(),
  occurredAt: z.string(),
  metadata: z.record(z.unknown()).default({}),
});
export type TimelineEntry = z.infer<typeof zTimelineEntry>;
